import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button, ToggleGroup, ToggleGroupItem } from '@shigo-ui/ui';

import { dashboardT } from '../../../../lib/messages';
import { createLiverampTargetingKey, LiverampGroupOperator } from '../../advancedTargetingModel';
import type { TargetingBuilderFormValue } from '../../builder/model';
import { LiverampRule } from './LiverampRule';
import type { LiverampSegmentClient } from './LiverampTargetingForm.types';

export function LiverampGroup({
  client,
  index,
  onRemove,
}: {
  client: LiverampSegmentClient;
  index: number;
  onRemove: () => void;
}) {
  'use no memo';

  const { control } = useFormContext<TargetingBuilderFormValue>();
  const groupPath =
    `liverampTargeting.groups.${String(index)}` as `liverampTargeting.groups.${number}`;
  const keys = useFieldArray({ control, name: `${groupPath}.keys` });
  return (
    <section className="border-input grid gap-4 rounded-md border p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">
            {dashboardT('targetingBuilder.liverampGroup', { number: index + 1 })}
          </h3>
          <p className="text-muted-foreground text-sm">{dashboardT('targetingBuilder.enforce')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Controller
            control={control}
            name={`${groupPath}.operator`}
            render={({ field }) => (
              <ToggleGroup
                type="single"
                variant="outline"
                value={field.value}
                aria-label={dashboardT('targetingBuilder.groupMatch')}
                onValueChange={(value) => {
                  if (value) field.onChange(value);
                }}
              >
                {Object.values(LiverampGroupOperator).map((value) => (
                  <ToggleGroupItem
                    key={value}
                    value={value}
                    aria-label={dashboardT(`targetingBuilder.groupOperators.${value}`)}
                  >
                    {dashboardT(`targetingBuilder.groupLabels.${value}`)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            )}
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={dashboardT('targetingBuilder.removeGroup')}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
      {keys.fields.map((key, keyIndex) => (
        <LiverampRule
          client={client}
          groupIndex={index}
          key={key.id}
          ruleIndex={keyIndex}
          onRemove={() => {
            keys.remove(keyIndex);
          }}
        />
      ))}
      <Button
        type="button"
        variant="ghost"
        className="w-fit"
        onClick={() => {
          keys.append(createLiverampTargetingKey());
        }}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {dashboardT('targetingBuilder.addSegmentRule')}
      </Button>
    </section>
  );
}
